
import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';

/**
 * A hook for retrieving a user's referral code
 */
export function useReferralCodes(userId?: string) {
  const [referralCode, setReferralCode] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // Referral code from session storage (from link)
  const [sourceReferralCode, setSourceReferralCode] = useState<string | null>(null);

  useEffect(() => {
    // Check if there's a referral code in session storage
    const storedCode = sessionStorage.getItem('referral_code');
    if (storedCode) {
      setSourceReferralCode(storedCode);
    }
  }, []);

  // Fetch the user's own referral code
  useEffect(() => {
    if (!userId) return;

    const fetchReferralCode = async () => {
      setIsLoading(true);
      setError(null);
      
      try {
        // Use session storage cache if available
        const cachedCode = sessionStorage.getItem(`referral_code_${userId}`);
        if (cachedCode) {
          setReferralCode(cachedCode);
          setIsLoading(false);
          return;
        }
        
        const { data, error } = await supabase
          .from('referral_codes')
          .select('code')
          .eq('user_id', userId)
          .maybeSingle();

        if (error) {
          console.error('Error fetching referral code:', error);
          setError(error.message);
        } else if (data?.code) {
          setReferralCode(data.code);
          // Cache the code in session storage
          sessionStorage.setItem(`referral_code_${userId}`, data.code);
        }
      } catch (error: unknown) {
        console.error('Error in fetchReferralCode:', error);
        setError(error instanceof Error ? error.message : String(error));
      } finally {
        setIsLoading(false);
      }
    };

    fetchReferralCode();
  }, [userId]);

  return {
    referralCode,
    sourceReferralCode,
    isLoading,
    error
  };
}
